import { giphyIndigo } from '@giphy/colors'
import { SyntheticEvent, useRef, useState } from 'react'
import styled from 'styled-components'
import CreationToolUploadField from '../../../types/upload-field'
import { UploadFieldBlock } from '../../blocks'
import { UtilityButton } from '../../buttons'
import CreationTag from '../../tag'
import Instructions from './field-instructions'
import Title from './field-title'
import {CreationToolUploadTag} from "../../upload/types";

const Wrapper = styled.div`
    display: flex;

    input[type='text'] {
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
    }
`

const AddButton = styled(UtilityButton)`
    background: ${giphyIndigo};
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    flex-shrink: 0;
    line-height: 38px;
    padding: 0;
    width: 36px;
`

const Tags = styled.div`
    margin-top: 15px;
`

const Tag = styled(CreationTag)`
    margin: 0 8px 10px 0;
`

type Props = {
    className?: string
    tags?: CreationToolUploadTag[]
    isBulk?: boolean
    showInstructions?: boolean
    field: CreationToolUploadField
    onRemove: (tag: string) => void
    onAdd: (tags: string[]) => void
    onToggleFeatured: (tag: string) => void
    enableFeatured: boolean
}

const CreationTagsField = ({
    className,
    tags = [],
    isBulk = false,
    showInstructions = false,
    field,
    onRemove,
    onAdd,
    onToggleFeatured,
    enableFeatured
}: Props) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const description = CreationToolUploadField.description(field)
    const [hasText, setHasText] = useState<boolean>(false)

    const setTags = () => {
        if (!inputRef.current) return

        // split tags by comma and add them to the list
        const tags = (inputRef.current.value || '').split(',').filter((tag) => tag !== '')
        inputRef.current.value = ''
        setHasText(false)
        if (tags.length) {
            return onAdd(tags)
        }
    }

    const onKeyDown = (e: SyntheticEvent<HTMLInputElement, KeyboardEvent>) => {
        e.nativeEvent.key === 'Enter' && setTags()
    }

    const onInput = (e: SyntheticEvent<HTMLInputElement, InputEvent>) => {
        setHasText(!!e.currentTarget.value)
    }

    return (
        <UploadFieldBlock className={className}>
            <Title field={field}>{isBulk ? ` (Bulk)` : ''}</Title>
            <Wrapper>
                <input
                    {...{
                        ref: inputRef,
                        type: 'text',
                        placeholder: description,
                        onKeyDown,
                        onInput,
                    }}
                />
                <AddButton className="ss-plus" disabled={!hasText} onClick={setTags} />
            </Wrapper>
            {tags.length > 0 && (
                <Tags>
                    {tags.map((tag) => (
                        <Tag
                            key={tag.text}
                            tag={tag.text}
                            onRemove={onRemove}
                            isFeatured={!!tag.isFeatured}
                            onToggleFeatured={onToggleFeatured}
                            enableFeatured={enableFeatured}
                        />
                    ))}
                </Tags>
            )}
            {showInstructions && <Instructions field={field} />}
        </UploadFieldBlock>
    )
}

export default CreationTagsField
